'use client'

/**
 * useFastDictation — instant on-device speech-to-text for the composer mic.
 *
 * Uses the browser Web Speech API (webkitSpeechRecognition) for live interim
 * text with near-zero latency and no server round trip, no LiveKit room, and
 * no conversational agent. This is plain dictation, not a realtime agent: it
 * never replies, never calls tools, just transcribes the user's own speech
 * and self-corrects interim → final per phrase.
 *
 * Not supported (Firefox, some mobile webviews) → isSupported=false and the
 * caller falls back to MediaRecorder → /api/speech-to-text (whole-file).
 */

import { useCallback, useEffect, useRef, useState } from 'react'

type SpeechAlt = { transcript?: string }
type SpeechResult = { isFinal: boolean; 0?: SpeechAlt; length: number }
type SpeechResultList = { length: number; [index: number]: SpeechResult }
type SpeechEvent = Event & { resultIndex: number; results: SpeechResultList }
type Recognition = {
  continuous: boolean
  interimResults: boolean
  lang: string
  onresult: ((event: SpeechEvent) => void) | null
  onerror: ((event: Event) => void) | null
  onend: (() => void) | null
  start: () => void
  stop: () => void
  abort: () => void
}

function getRecognitionCtor(): (new () => Recognition) | null {
  if (typeof window === 'undefined') return null
  const w = window as unknown as {
    SpeechRecognition?: new () => Recognition
    webkitSpeechRecognition?: new () => Recognition
  }
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null
}

export function useFastDictation() {
  const ctorRef = useRef<(new () => Recognition) | null>(null)
  if (ctorRef.current === null) ctorRef.current = getRecognitionCtor()

  const [isSupported] = useState<boolean>(() => Boolean(getRecognitionCtor()))
  const [isListening, setIsListening] = useState(false)
  const [interimText, setInterimText] = useState('')

  const recognitionRef = useRef<Recognition | null>(null)
  const finalRef = useRef('')
  const interimRef = useRef('')
  const wantActiveRef = useRef(false)

  const fullText = useCallback(
    () => `${finalRef.current}${interimRef.current}`.replace(/\s+/g, ' ').trim(),
    []
  )

  const teardown = useCallback(() => {
    wantActiveRef.current = false
    const recognition = recognitionRef.current
    recognitionRef.current = null
    if (recognition) {
      recognition.onresult = null
      recognition.onerror = null
      recognition.onend = null
      try {
        recognition.stop()
      } catch {
        try {
          recognition.abort()
        } catch {
          /* ignore */
        }
      }
    }
    setIsListening(false)
  }, [])

  useEffect(() => () => teardown(), [teardown])

  const start = useCallback(() => {
    const Ctor = ctorRef.current
    if (!Ctor) return
    // Reset transcript for a fresh dictation.
    finalRef.current = ''
    interimRef.current = ''
    setInterimText('')

    const recognition = new Ctor()
    recognition.continuous = true
    recognition.interimResults = true
    recognition.lang =
      (typeof navigator !== 'undefined' && navigator.language) || 'en-US'

    recognition.onresult = (event: SpeechEvent) => {
      let interim = ''
      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        const result = event.results[i]
        const text = result?.[0]?.transcript ?? ''
        if (result?.isFinal) {
          finalRef.current = `${finalRef.current}${text} `
        } else {
          interim += text
        }
      }
      interimRef.current = interim
      setInterimText(fullText())
    }

    recognition.onerror = () => {
      // 'no-speech' / 'aborted' are normal; keep whatever we captured.
    }

    recognition.onend = () => {
      // Browsers auto-stop on silence; restart while the user is still holding
      // the mic so continuous dictation does not cut off mid-thought.
      interimRef.current = ''
      if (wantActiveRef.current && recognitionRef.current === recognition) {
        try {
          recognition.start()
          return
        } catch {
          /* fall through to stop */
        }
      }
      setIsListening(false)
    }

    recognitionRef.current = recognition
    wantActiveRef.current = true
    try {
      recognition.start()
      setIsListening(true)
    } catch {
      teardown()
    }
  }, [fullText, teardown])

  const stop = useCallback((): string => {
    const text = fullText()
    teardown()
    return text
  }, [fullText, teardown])

  return { isSupported, isListening, interimText, start, stop }
}
